import { useGetAlbumAnalyticsApiV1Suspense } from "@/api/__generated__/api.schema";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { AlbumDetail } from "@/routes/custom-music/dsp-analytics/components/details/album-detail";
import { AlbumFlag } from "@/routes/custom-music/dsp-analytics/components/graphics/album-flag";
import { RevenueBreakdownTable } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-breakdown-table";
import { RevenueChart } from "@/routes/custom-music/dsp-analytics/components/graphics/revenue-chart";
import { useAnalyticsMonth } from "@/routes/custom-music/dsp-analytics/context/dsp-analytics-context";
import { getPreviousMonthRange } from "@/utils/date";
import { formatMonthString } from "@/utils/time";
import { Button, Icon, Sheet, SheetContent, Typography } from "@backpac/monica";
import { useState } from "react";

export const AlbumAnalytics = () => {
  const { selectedMonthRange } = useAnalyticsMonth();
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: platformAnalytics },
    },
  } = useGetAlbumAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(selectedMonthRange[0]),
    to: formatMonthString(selectedMonthRange[1]),
  });
  // previous revenue with the same length as the current revenue
  const {
    data: {
      data: { items: previousPlatformAnalytics },
    },
  } = useGetAlbumAnalyticsApiV1Suspense(workspaceId, {
    from: formatMonthString(getPreviousMonthRange(selectedMonthRange)[0]),
    to: formatMonthString(getPreviousMonthRange(selectedMonthRange)[1]),
  });

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    platformAnalytics.slice(0, 5).map((item) => item.name),
  );
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  return (
    <div className="flex flex-col gap-6">
      <AlbumAnalytics.AlbumDetailSheet selectedCode={selectedCode} setSelectedCode={setSelectedCode} />
      <RevenueChart
        analytics={platformAnalytics}
        previousAnalytics={previousPlatformAnalytics}
        selectedIds={selectedIds}
      />
      <RevenueBreakdownTable
        title="Album"
        analytics={platformAnalytics}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        additionalColumns={[
          {
            label: "Album",
            render: (item) => <AlbumFlag coverImageUrl={item.coverImageUrl} name={item.name} />,
            position: "start",
          },
          {
            label: "Artist",
            render: (item) => (
              <Typography size="xs" color="textPrimary">
                {item.artist}
              </Typography>
            ),
            position: "start",
          },
          {
            label: "",
            render: (item) => (
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedCode(item.code);
                }}
                rightAddon={<Icon name="arrow-right" iconSize="12px" iconColor="textPrimary" />}
              >
                <Typography size="xs" color="textPrimary" weight={600}>
                  View Details
                </Typography>
              </Button>
            ),
            position: "end",
          },
        ]}
      />
    </div>
  );
};

interface AlbumDetailSheetProps {
  selectedCode: string | null;
  setSelectedCode: (code: string | null) => void;
}

AlbumAnalytics.AlbumDetailSheet = ({ selectedCode, setSelectedCode }: AlbumDetailSheetProps) => {
  return (
    <Sheet open={!!selectedCode} onOpenChange={() => setSelectedCode(null)}>
      <SheetContent className="pt-16 px-0 sm:max-w-[800px] bg-backgroundLight overflow-y-auto">
        {selectedCode != null ? <AlbumDetail code={selectedCode} /> : <></>}
      </SheetContent>
    </Sheet>
  );
};
